import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedNumber } from 'react-intl';
import { Box, Button, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import pMap from 'p-map';

import { BASE_API_URL } from '../../lib/api';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { selectors as growerSelectors } from '../../store/dux/grower-dux';
import { Table } from '../../components/table/Table';
import { Dropzone } from '../../components/Dropzone';
import { Section } from '../Section';

export default function FieldBoundaryUpload() {
  const growerId = useSelector(growerSelectors.selectedId);
  const growerName = useSelector(growerSelectors.selectedName);
  const [results, setResults] = useState([]);
  const [uploadingFile, setUploadingFile] = useState('');
  const [isConfirming, setConfirming] = useState(false);
  const [files, setFiles] = useState([]);
  const isWorking = Boolean(uploadingFile);

  return (
    <Section>
      {buildConfirmation()}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          '.MuiChip-root': { my: 1, mx: 3 },
        }}
      >
        <Dropzone
          onChange={setFiles}
          contentType="boundaries"
          fileType="zip"
          isUploading={Boolean(uploadingFile)}
        />
        <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
          {buildUploadButton()}
          {buildProgress()}
          {buildResults()}
        </Box>
      </Box>
    </Section>
  );

  function buildUploadButton() {
    return (
      <Box sx={{ pb: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          size="large"
          color="inherit"
          variant="outlined"
          disabled={!files.length || isWorking}
          onClick={() => setConfirming(true)}
        >
          Upload
        </Button>
      </Box>
    );
  }

  function buildProgress() {
    if (!uploadingFile) return null;

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pb: 3 }}>
        <Typography>Uploading {uploadingFile}</Typography>
        <Typography className="text-loading"></Typography>
      </Box>
    );
  }

  function buildResults() {
    if (!results.length) return null;

    return (
      <>
        <Box sx={{ flex: 1 }}>
          <Table
            hideFooter={false}
            columns={buildColumns()}
            rows={results}
            localeText={{ footerTotalRows: 'Total Fields Uploaded:' }}
            initialState={{
              sorting: {
                sortModel: [
                  { field: 'failure', sort: 'desc' },
                  { field: 'farmName', sort: 'asc' },
                  { field: 'fieldName', sort: 'asc' },
                ],
              },
            }}
          />
        </Box>
      </>
    );
  }

  function buildColumns() {
    return [
      {
        field: 'failure',
        headerName: 'Result',
        flex: 0.75,
        renderCell: ({ row }) => {
          if (!row.failure) return <CheckCircleOutlineIcon color="primary" />;
          return (
            <>
              <ErrorOutlineIcon color="error" />
              <Box component="span" sx={{ pl: 1 }}>
                {row.failure}
              </Box>
            </>
          );
        },
      },
      {
        field: 'farmName',
        headerName: 'Farm',
        flex: 1,
      },
      {
        field: 'fieldName',
        headerName: 'Field',
        flex: 1,
      },
      {
        field: 'acres',
        headerName: 'Acres',
        flex: 0.5,
        renderCell: ({ row }) =>
          row.acres && (
            <FormattedNumber value={row.acres} maximumFractionDigits={2} />
          ),
      },
      {
        field: 'fileName',
        headerName: 'File',
        flex: 1,
      },
    ];
  }

  function buildConfirmation() {
    if (!isConfirming) return null;

    return (
      <ConfirmationDialog
        title={`Upload Files for ${growerName}?`}
        titleIcon={false}
        handleClose={setConfirming.bind(null, false)}
        handleConfirm={handleUpload}
        message={
          <span>
            Are you sure you want to upload for <b>{growerName}</b>?
          </span>
        }
      />
    );
  }

  async function handleUpload() {
    setResults([]);
    setConfirming(false);
    await pMap(files, uploadOne, { concurrency: 1 });
    setUploadingFile(null);
  }

  async function uploadOne(file) {
    setUploadingFile(file.name);

    const url = `${BASE_API_URL}/${growerId}/boundaries`;

    const formData = new FormData();
    formData.append('file', file);
    setUploadingFile(file.name);

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const fileResults = await response.json();

    setResults((current) => [
      ...current,
      ...fileResults.map((r) => ({ fileName: file.name, ...r })),
    ]);
  }
}
